import React from 'react';

import { type HexColor, contrastRatio, luminance, testWCAG } from 'common/colors/utils';
import PostVotesV3 from 'common/post/PostVotes/PostVotesUI';
import Badge from 'common/uiV3/Badge';
import Flex from 'common/uiV3/Flex';
import HStack from 'common/uiV3/HStack';
import VStack from 'common/uiV3/VStack';
import hexToRGB from 'common/util/hexToRGB';
import roundCents from 'common/util/roundCents';
import { Check, X } from 'lucide-react';

type Props = {
  foreground: HexColor;
  backgrounds: Array<HexColor>;
};

const darkTheme = {
  '--color-foreground': '#dddddd',
  '--color-gray-110': '#b4b4b4',
  '--color-gray-30': '#222222',
  '--color-gray-50': '#313131',
  '--color-grass-110': '#63c174',
  '--color-grass-30': '#132819',
  '--color-red-90': '#e5484d',
  '--color-red-30': '#3c181a',
} as React.CSSProperties;

const lightTheme = {
  '--color-foreground': '#202020',
  '--color-gray-110': '#646464',
  '--color-gray-30': '#f0f0f0',
  '--color-gray-50': '#e0e0e0',
  '--color-grass-110': '#297c3b',
  '--color-grass-30': '#ebf9eb',
  '--color-red-90': '#e5484d',
  '--color-red-30': '#ffefef',
} as React.CSSProperties;

const ColorAccessibilityChecker = ({ foreground, backgrounds }: Props) => {
  return (
    <HStack className="box-border rounded overflow-hidden base-border">
      {backgrounds.map((background) => {
        const rgbForeground = hexToRGB(foreground);
        const rgbBackground = hexToRGB(background);

        if (!rgbBackground || !rgbForeground) {
          return null;
        }

        const contrast = contrastRatio(rgbForeground, rgbBackground);
        const contrastChecks = testWCAG(rgbForeground, rgbBackground);
        const brightness = luminance(rgbBackground);
        const isDark = brightness < 0.3;

        return (
          // Inline theme variables to override admin theme and force preview theme
          <div key={background} style={isDark ? darkTheme : lightTheme} className="w-full">
            <VStack className="flex-1 gap-4 justify-center p-4" style={{ background }}>
              <Flex className="items-center gap-4">
                <PostVotesV3 highlight={foreground} onClick={() => null} score={1200} />
                <div>
                  <p className="text-base" style={{ color: foreground }}>
                    This is some sample 14pt text
                  </p>
                  <p className="text-xl" style={{ color: foreground }}>
                    This is some sample 18pt text
                  </p>
                </div>
              </Flex>
              <Flex className="gap-2">
                <Badge variant="info">{roundCents(1 / contrast)}</Badge>
                <Badge variant={contrastChecks.AA.smallText ? 'success' : 'destructive'}>
                  Small text {contrastChecks.AA.smallText ? <Check /> : <X />}
                </Badge>
                <Badge variant={contrastChecks.AA.largeText ? 'success' : 'destructive'}>
                  Large text {contrastChecks.AA.largeText ? <Check /> : <X />}
                </Badge>
              </Flex>
            </VStack>
          </div>
        );
      })}
    </HStack>
  );
};

export default ColorAccessibilityChecker;
